import React, { useCallback } from 'react';
import { Switch, Tooltip } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import DragNDropHelpers from '../../services/helpers/DragNDropHelpers';
import SortableList from '../dnd/SortableList';
import SortableItem from '../dnd/SortableItem';
import {
  DELIVERY_BRAND_BY_PROVIDER,
  DeliveryProvider,
  PickupTimeOptimizationStrategy,
} from '../../services/exports/Constants';

export default function DeliveryProvidersList({
  data,
  onChange,
  supportedDeliveryProviders,
  isProviderConnected = () => true,
  getProviderNotice = () => null,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:DeliveryTab' });

  const { delivery_providers, pickup_time_optimization_strategies } = data;

  const toggleDeliveryProviderAvailable = (deliveryProvider) =>
    onChange(
      'delivery_providers',
      delivery_providers.includes(deliveryProvider)
        ? delivery_providers.filter((item) => item !== deliveryProvider)
        : [...delivery_providers, deliveryProvider],
    );

  const onDragEnd = (event) => {
    DragNDropHelpers.onDndKitDragEnd(supportedDeliveryProviders, event, (res) => {
      const available = res.filter((provider) => delivery_providers.includes(provider));

      onChange('delivery_providers', available);
    });
  };

  const getProviderOptimizationStrategy = useCallback(
    (provider) => {
      if (!pickup_time_optimization_strategies || !pickup_time_optimization_strategies[provider]) {
        return (
          {
            [DeliveryProvider.Standalone]: PickupTimeOptimizationStrategy.WaitForFirstUpdate,
            [DeliveryProvider.WoltDrive]: PickupTimeOptimizationStrategy.WaitForFirstUpdate,
          }[provider] ?? PickupTimeOptimizationStrategy.WaitForDriverAssignment
        );
      }

      return pickup_time_optimization_strategies[provider];
    },
    [pickup_time_optimization_strategies],
  );

  const onProviderOptimizationStrategyChange = (provider, value) =>
    onChange('pickup_time_optimization_strategies', {
      ...(pickup_time_optimization_strategies ?? {}),
      [provider]: value,
    });

  return (
    <SortableList items={supportedDeliveryProviders} onDragEnd={onDragEnd}>
      {supportedDeliveryProviders.map((item) => (
        <SortableItem
          key={item}
          id={item}
          disabled={delivery_providers.length === 1 || !delivery_providers.includes(item) || !isProviderConnected(item)}
        >
          {({ dragHandleProps }) => (
            <div className="flex items-center mb-md">
              <span className="flex" {...dragHandleProps}>
                <HolderOutlined className="mr-lg text-black" />
              </span>
              {/* Unconnected: never shown as on, since the backend skips it at runtime anyway. The
                  span takes the hover: a disabled switch swallows the pointer leave, which left the
                  tooltip open after the mouse had moved on. */}
              <Tooltip
                title={
                  getProviderNotice(item)
                    ? t(`sections.delivery_mode.fields.provider_not_connected.${getProviderNotice(item)}`, {
                        provider: DELIVERY_BRAND_BY_PROVIDER[item],
                      })
                    : undefined
                }
              >
                <span className={isProviderConnected(item) ? 'inline-flex' : 'inline-flex cursor-not-allowed'}>
                  <Switch
                    id={`delivery-provider-${item}`}
                    className={isProviderConnected(item) ? undefined : 'pointer-events-none'}
                    checked={isProviderConnected(item) && delivery_providers.includes(item)}
                    disabled={!isProviderConnected(item)}
                    onChange={() => toggleDeliveryProviderAvailable(item)}
                  />
                </span>
              </Tooltip>
              <label htmlFor={`delivery-provider-${item}`} className="my-auto ml-lg cursor-pointer">
                {DELIVERY_BRAND_BY_PROVIDER[item]}
              </label>
              <div className="ml-lg pl-lg border-l border-black border-solid border-y-0 border-r-0 flex">
                <Switch
                  id={`delivery-provider-${item}-instant_print_enabled`}
                  checked={getProviderOptimizationStrategy(item) === PickupTimeOptimizationStrategy.WaitForFirstUpdate}
                  onChange={(checked) =>
                    onProviderOptimizationStrategyChange(
                      item,
                      checked
                        ? PickupTimeOptimizationStrategy.WaitForFirstUpdate
                        : PickupTimeOptimizationStrategy.WaitForDriverAssignment,
                    )
                  }
                />
                <label
                  htmlFor={`delivery-provider-${item}-instant_print_enabled`}
                  className="my-auto ml-lg cursor-pointer"
                >
                  {t('sections.delivery_mode.fields.instant_print_enabled.label')}
                </label>
              </div>
            </div>
          )}
        </SortableItem>
      ))}
    </SortableList>
  );
}
